<script setup>
import { computed } from 'vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import MediaflowDock from './MediaflowDock.vue'
import MediaflowMenu from './MediaflowMenu.vue'
import MediaflowFlowThumb from './MediaflowFlowThumb.vue'
import MediaflowDockEmpty from './MediaflowDockEmpty.vue'
import {
	overlayRows, mediaTypeMeta, ENGINE_HINT, DOCK_DANGER_ITEM,
} from './mediaflowMeta'

/**
 * Graphics dock: production-level overlays (logo, lower thirds, tickers…).
 * They stay on air across scene switches; the switch takes one on / off air
 * with its own transition. Shift+1–9 follow the order shown here. Group
 * headers appear only when graphics use more than one group (`row`).
 */
const props = defineProps({
	desk: { type: Object, required: true },
	canvas: { type: Object, required: true },
	enabled: { type: Boolean, default: true },
	/** Graphics switched on before going live are cued, not on air. */
	sessionActive: { type: Boolean, default: false },
	mobile: { type: Boolean, default: false },
	readOnly: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle', 'position', 'settings', 'delete', 'move', 'add'])

const overlays = computed(() => props.desk.overlays.value)
const groups = computed(() => overlayRows(overlays.value))
const flat = computed(() => groups.value.flatMap((g) => g.items))
const capable = computed(() => props.desk.capabilities.value.overlays)
const editingId = computed(() => props.desk.editingOverlayId.value)
const takingId = computed(() => props.desk.takingOverlayId.value)
const liveCount = computed(() => overlays.value.filter((o) => o.live).length)
const liveLabel = computed(() => (props.sessionActive ? 'On air' : 'Cued'))
/** On / off after Apply (the switch shows this); Program keeps `overlay.live`. */
const target = (overlay) => (props.desk.overlayTarget ? props.desk.overlayTarget(overlay) : !!overlay.live)
const pending = (overlay) => (props.desk.overlayPending ? props.desk.overlayPending(overlay) : null)
const pendingLabel = (overlay) => (pending(overlay) === 'on' ? 'On after Apply' : 'Off after Apply')
const switchLabel = (overlay) => {
	const on = target(overlay)
	if (!props.sessionActive) return on ? `Don't show ${overlay.name} when you go live` : `Show ${overlay.name} when you go live`
	if (props.desk.takesDrafted?.value) return on ? `Take ${overlay.name} off air when you apply` : `Put ${overlay.name} on air when you apply`
	return on ? `Take ${overlay.name} off air` : `Put ${overlay.name} on air`
}

const takeReason = computed(() => {
	if (!props.enabled) return 'Enable the production first'
	if (!capable.value) return ENGINE_HINT
	return ''
})
const addReason = takeReason

const hotkey = (overlay) => {
	const i = flat.value.indexOf(overlay)
	return !props.mobile && i >= 0 && i < 9 ? `⇧${i + 1}` : ''
}
const meta = (overlay) => {
	const t = overlay.transition || {}
	return `${mediaTypeMeta(overlay.media?.type).label} · ${t.type === 'Fade' ? `Fade ${t.duration || 300} ms` : 'Cut'}`
}
const posInGroup = (overlay) => {
	const g = groups.value.find((x) => x.items.includes(overlay))
	return { index: g ? g.items.indexOf(overlay) : 0, count: g ? g.items.length : 1 }
}
</script>

<template>
	<MediaflowDock
		title="Graphics"
		:count="String(overlays.length)"
		:count-label="`${liveCount} ${sessionActive ? 'on air' : 'cued'}`"
		testid="dock-graphics"
		:locked="!capable"
		:locked-reason="ENGINE_HINT"
		:add-label="readOnly ? '' : 'Add graphic'"
		:add-disabled-reason="addReason"
		@add="emit('add')"
	>
		<template #context>Shown over every scene</template>
		<MediaflowDockEmpty v-if="!overlays.length" text="No graphics yet." :action-label="readOnly ? '' : 'Add graphic'" :disabled-reason="readOnly ? '' : addReason" @add="emit('add')" />
		<div v-else class="flex flex-col gap-2">
			<div v-for="group in groups" :key="group.name" class="flex flex-col gap-0.5" :data-group="group.name">
				<h3 v-if="groups.length > 1" class="m-0 px-1.5 pb-0.5 pt-1 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6" data-testid="graphics-group">{{ group.name }}</h3>
				<ul class="m-0 flex list-none flex-col gap-1.5 p-0">
					<li
						v-for="overlay in group.items"
						:key="overlay._id"
						class="mf-row flex items-center gap-2 rounded-lg p-1 pr-0"
						:class="overlay.live ? 'bg-error-transparent ring-1 ring-error' : pending(overlay) ? 'ring-1 ring-gold' : overlay._id === editingId ? 'bg-main-transparent ring-1 ring-main' : ''"
						:data-tone="overlay.live ? 'live' : overlay._id === editingId ? 'editing' : undefined"
						data-testid="graphic-row"
						:data-overlay-id="overlay._id"
						:data-live="overlay.live ? 'true' : undefined"
						:data-pending="pending(overlay) || undefined"
					>
						<span class="w-11 shrink-0 overflow-hidden rounded ring-1 ring-surface-4"><MediaflowFlowThumb :media="overlay.media" :canvas="canvas" :dim="!target(overlay)" fit /></span>
						<span class="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
							<span class="truncate text-[0.8rem] font-medium text-surface-text-2" :title="overlay.name">{{ overlay.name }}</span>
							<span class="flex min-w-0 items-center gap-1 text-[0.7rem] text-surface-8">
								<kbd v-if="hotkey(overlay) && !pending(overlay)" class="shrink-0 rounded bg-surface-3 px-1 font-mono text-[0.6rem] text-surface-text-1" :title="`Shift + ${hotkey(overlay).slice(1)} toggles this graphic`">{{ hotkey(overlay) }}</kbd>
								<span v-if="pending(overlay)" class="inline-flex min-w-0 items-center gap-1 font-medium text-gold" :title="`${overlay.live ? liveLabel : 'Off air'} on Program now`" data-testid="graphic-pending">
									<span class="size-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" /><span class="truncate">{{ pendingLabel(overlay) }}</span>
								</span>
								<span v-else class="truncate" :class="overlay.live ? 'font-medium text-error-light' : ''">{{ overlay.live ? liveLabel : meta(overlay) }}</span>
							</span>
						</span>
						<Tooltip :content="takeReason || switchLabel(overlay)" wrapper-class="inline-flex" wrap-trigger>
							<FormSwitch
								:key="`${overlay._id}-${target(overlay)}`"
								size="md"
								class="graphic-switch"
								:class="pending(overlay) ? 'graphic-switch--pending' : ''"
								:model-value="target(overlay)"
								:disabled="!!takeReason || takingId === overlay._id"
								:aria-label="switchLabel(overlay)"
								data-testid="graphic-switch"
								@update:model-value="emit('toggle', overlay)"
							/>
						</Tooltip>
						<MediaflowMenu v-if="!readOnly || !mobile" :aria-label="`Actions for ${overlay.name}`" list-classes="min-w-[210px]">
							<DropDownMenuItem v-if="!mobile" :disabled="!enabled" @click="emit('position', overlay)">Edit position on Preview</DropDownMenuItem>
							<template v-if="!readOnly">
								<DropDownMenuItem :disabled="!enabled" @click="emit('settings', overlay)">Name, group &amp; transition…</DropDownMenuItem>
								<DropDownMenuItem :disabled="!enabled || posInGroup(overlay).index === 0" @click="emit('move', overlay, -1)">Move up (in front)</DropDownMenuItem>
								<DropDownMenuItem :disabled="!enabled || posInGroup(overlay).index === posInGroup(overlay).count - 1" @click="emit('move', overlay, 1)">Move down (behind)</DropDownMenuItem>
								<DropDownMenuItem :disabled="!enabled" :custom-classes="DOCK_DANGER_ITEM" @click="emit('delete', overlay)">Delete</DropDownMenuItem>
							</template>
						</MediaflowMenu>
					</li>
				</ul>
			</div>
		</div>
	</MediaflowDock>
</template>

<style scoped>
/* On air = red tally, not the generic "enabled" green. */
.graphic-switch :deep(.switcher:checked::before) {
	background-color: var(--error);
}
/* Staged, not on Program yet: the draft colour, like the unsaved-changes dot. */
.graphic-switch--pending :deep(.switcher:checked::before) {
	background-color: var(--gold-custom);
}
</style>
